<script setup lang="ts">
import { createFinancials, groupApplicantsByEntity } from '@/helpers/builder';
import { EmploymentType } from '@/helpers/const';
import { getFullName } from '@/helpers/format';
import { shouldValidateIncomeForm, validateIncomeForms } from '@/helpers/incomeFormValidation';
import { resolveMarriedPartnerId } from '@/helpers/resolveMarriedPartnerId';
import { useRequestIncomeExpenseProcess } from '@/helpers/useRequestIncomeExpenseProcess';
import type { ApplicantFinancials, ApplicantId } from '@/types';
import { CheckIcon, XMarkIcon } from '@heroicons/vue/20/solid';
import { useTemplateRefsList } from '@vueuse/core';
import { cloneDeep } from 'lodash-es';
import { nextTick, ref } from 'vue';
import IncomeForm from './modules/Income/IncomeForm.vue';

const { authorisedUser, loanApp, loanApplicants, saveAndGoNext, goBack } = useRequestIncomeExpenseProcess();

const isSecondaryDeclaration = false;
const error = ref('');

// Every applicant on the loan is shown (so co-borrowers under the same company/trust entity
// appear, regardless of user type); editing is gated by canEditApplicantIncome.
const validApplicants = loanApp.value.applicants.map((a) => a.id);
const initFinancials = validApplicants.reduce((obj, item) => {
  obj[item] = createFinancials();
  return obj;
}, {} as ApplicantFinancials);
const financials = ref({ ...initFinancials, ...cloneDeep(loanApp.value.financials) });

// Entity-first layout: company/trust borrowers become section headers with their members
// nested beneath; applicants in no entity fall into a trailing un-titled section. A member of
// several entities is listed under each, but only their anchor occurrence renders a form —
// `orderedApplicants` is the anchor render order, which IncomeForm refs are collected in, so
// validation indexes against it (not filteredValidApplicants).
const entitySections = groupApplicantsByEntity(validApplicants, loanApp.value.borrowerGroups, loanApp.value.applicants);
const orderedApplicants = entitySections.flatMap((section) =>
  section.members.filter((member) => member.isDetailAnchor).map((member) => member.applicantId),
);

// Editable: the logged-in user's own income, plus a married/defacto partner's. Every other
// co-borrower is shown read-only — they declare their own income via the email request.
// Resolve the partner directly from the marriage relationship (either direction) rather than via a
// "primary" applicant: under the borrower-centric model an application can have no primary applicant
// (both partners are secondaries under a company/trust borrower), which previously left the partner
// unresolved and un-editable.
const partnerId = resolveMarriedPartnerId(authorisedUser.id, loanApp.value.applicants);

// An applicant who has already submitted (declared) is locked — view-only for everyone,
// including a partner re-opening the household after the other member submitted.
const hasSubmitted = (applicantId: ApplicantId): boolean =>
  (loanApp.value.declaredApplicantIds ?? []).includes(applicantId);

// A married household is submitted as a unit (one member submits both incomes), so once either
// member has declared the whole household is locked — view-only for the partner logging in after.
const householdSubmitted = hasSubmitted(authorisedUser.id as ApplicantId) || (!!partnerId && hasSubmitted(partnerId));

const canEditApplicantIncome = (applicantId: ApplicantId): boolean => {
  if (loanApp.value.userType === 'Broker') return true;
  if (hasSubmitted(applicantId)) return false;
  if (householdSubmitted) return false;
  if (applicantId === authorisedUser.id) return true;
  if (partnerId && applicantId === partnerId) return true;
  return false;
};

// Render order of the editable forms only — IncomeForm refs are collected in this order
// (read-only members render no form), so validation indexes against it.
const editableApplicants = orderedApplicants.filter((id) => canEditApplicantIncome(id));

// Co-borrowers outside the user's household declare their own income via the email request.
const otherApplicantNames = loanApp.value.applicants
  .filter((a) => a.id !== authorisedUser.id && a.id !== partnerId)
  .map((a) => a.preferredName || a.firstName || '')
  .filter(Boolean);

// The logged-in applicant must provide their own income. A broker only validates forms they opened
// or that already contain data, so an unrelated untouched applicant cannot block the request.
const requiredIncomeApplicantId =
  loanApp.value.userType === 'Broker'
    ? undefined
    : editableApplicants.find((id) => id === authorisedUser.id) ??
      loanApp.value.applicants.map((a) => a.id).find((id) => editableApplicants.includes(id));

const incomeRefs = useTemplateRefsList<InstanceType<typeof IncomeForm>>();
const activeTab = ref<ApplicantId | undefined>(editableApplicants.length === 1 ? editableApplicants[0] : undefined);
const openedApplicantIds = new Set<ApplicantId>(activeTab.value ? [activeTab.value] : []);
const invalidEmploymentId = ref();

const openIncomeForm = (applicantId: ApplicantId) => {
  openedApplicantIds.add(applicantId);
  activeTab.value = applicantId;
};

const cancelIncomeForm = (applicantId: ApplicantId) => {
  openedApplicantIds.delete(applicantId);
  activeTab.value = undefined;
};

const validateIncome = async () => {
  const validation = await validateIncomeForms({
    applicantIds: editableApplicants,
    forms: incomeRefs.value,
    shouldValidate: (applicantId) =>
      shouldValidateIncomeForm(openedApplicantIds.has(applicantId), financials.value[applicantId]?.income ?? null),
  });

  if (!validation.validated) {
    openIncomeForm(validation.applicantId);
    invalidEmploymentId.value = validation.employmentId;
    error.value = `${getFullName(
      loanApplicants.value[validation.applicantId],
    )}'s income details are incomplete. Please review the highlighted fields.`;
    await nextTick();
    validation.form.scrollToFirstError();
    return false;
  }

  return true;
};

const saveAndContinue = async () => {
  if (await validateIncome()) {
    if (requiredIncomeApplicantId && !financials.value[requiredIncomeApplicantId]?.income?.employment.length) {
      error.value = 'Income source is required.';
      openIncomeForm(requiredIncomeApplicantId);
      await nextTick();
      incomeRefs.value[editableApplicants.indexOf(requiredIncomeApplicantId)]?.scrollToFirstError();
      return;
    }
    error.value = '';
    return saveAndGoNext({
      financials: financials.value,
    });
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cardTitle = (employment: any) => {
  let description = '';
  if (employment.type === EmploymentType.SELF_EMPLOYED) {
    description = employment.tradingName;
  } else if (
    employment.type === EmploymentType.FULL_TIME ||
    employment.type === EmploymentType.PART_TIME ||
    employment.type === EmploymentType.CASUAL
  ) {
    description = employment.employerName;
  }
  return `${employment.type.charAt(0).toUpperCase() + employment.type.slice(1).replace('_', ' ')}: ${description}`;
};

const cardDescription = (applicantId: ApplicantId) => {
  const applicantFinancials = financials.value[applicantId];
  if (!applicantFinancials || !applicantFinancials.income) {
    console.warn(`Financials or income not found for applicantId: ${applicantId}`);
    return [];
  }

  return applicantFinancials.income.employment?.map((item) => cardTitle(item)) || [];
};
</script>

<template>
  <div class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        What are your
        <BxHighlight>income</BxHighlight>
        sources?
      </h2>
    </div>
    <div v-if="loanApp.applicants.length > 1 && loanApp.actionType === 'createNew'" class="my-6">
      <p v-if="otherApplicantNames.length">
        Only include your income, we will request this information from {{ otherApplicantNames.join(', ') }} via email.
      </p>
    </div>
    <div class="my-6 grid gap-6">
      <div
        v-for="section in entitySections"
        :key="section.entity?.id ?? 'individuals'"
        :class="section.entity && 'rounded-lg border border-gray-200 bg-gray-50 p-4 sm:p-6'"
      >
        <h3 v-if="section.entity" class="mb-4 text-xl font-semibold text-bridgit-navy">
          {{ section.entity.name }}
        </h3>
        <p v-if="section.entity && !section.members.length" class="text-sm text-gray-500">
          No individual applicants are linked to this entity.
        </p>
        <div class="grid gap-4">
          <div v-for="member in section.members" :key="member.applicantId">
            <div v-if="!member.isDetailAnchor" class="text-sm text-gray-500">
              {{ getFullName(loanApplicants[member.applicantId]) }}
              <span v-if="member.roleLabel">· {{ member.roleLabel }}</span>
              <span v-if="member.partnerName" class="font-normal text-gray-500"
                >(Married to {{ member.partnerName }})</span
              >
            </div>
            <BxCard
              v-if="member.isDetailAnchor && canEditApplicantIncome(member.applicantId)"
              :class="member.applicantId !== activeTab && 'hidden'"
            >
              <h2 class="mb-1 text-xl font-medium">
                {{ getFullName(loanApplicants[member.applicantId]) }}
                <span v-if="member.roleLabel" class="text-base font-normal text-gray-500">
                  · {{ member.roleLabel }}
                </span>
                <span v-if="member.partnerName" class="text-base font-normal text-gray-500"
                  >(Married to {{ member.partnerName }})</span
                >
              </h2>
              <IncomeForm
                :ref="incomeRefs.set"
                v-model="financials[member.applicantId].income"
                :applicant-id="member.applicantId"
                :animate="member.applicantId === activeTab"
                :show-buttons="orderedApplicants.length >= 1"
                :invalid-employment-id="invalidEmploymentId"
                @save="activeTab = undefined"
                @cancel="cancelIncomeForm(member.applicantId)"
              />
            </BxCard>
            <BxCard
              v-if="member.isDetailAnchor"
              :class="`${member.applicantId === activeTab && 'hidden'} employment-card`"
            >
              <div class="flex items-center">
                <div class="card-details">
                  <div class="mb-1 font-medium">
                    {{ getFullName(loanApplicants[member.applicantId]) }}
                    <span v-if="member.roleLabel" class="font-normal text-gray-500">· {{ member.roleLabel }}</span>
                    <span v-if="member.partnerName" class="font-normal text-gray-500"
                      >(Married to {{ member.partnerName }})</span
                    >
                  </div>
                  <div class="flex items-center gap-2">
                    <div class="h-5 w-5">
                      <CheckIcon
                        v-if="financials[member.applicantId].income?.employment.length"
                        class="text-green-600"
                      />
                      <XMarkIcon v-else class="text-rose-600" />
                    </div>
                    Employment details
                  </div>
                  <div class="flex items-center gap-2">
                    <div class="h-5 w-5">
                      <CheckIcon
                        v-if="financials[member.applicantId].income?.expectIncomeChange !== undefined"
                        class="text-green-600"
                      />
                      <XMarkIcon v-else class="text-rose-600" />
                    </div>
                    Additional income
                  </div>
                </div>
                <div class="employment-descriptions ml-6">
                  <div>
                    <div v-for="description in cardDescription(member.applicantId)" :key="description">
                      <span class="text-gray-400">{{ description }}</span>
                    </div>
                  </div>
                </div>
                <div v-if="canEditApplicantIncome(member.applicantId)" class="card-button">
                  <BxButton
                    :id="member.applicantId + '-add-income'"
                    variant="secondary"
                    :disabled="
                      (activeTab && activeTab !== member.applicantId) ||
                      (loanApplicants[member.applicantId].type === 'primary' && isSecondaryDeclaration)
                    "
                    @click="openIncomeForm(member.applicantId)"
                  >
                    {{ financials[member.applicantId]?.income?.employment.length ? 'Edit' : 'Add' }} income
                  </BxButton>
                </div>
              </div>
            </BxCard>
            <BxCard
              v-if="member.isDetailAnchor"
              :class="`${member.applicantId === activeTab && 'hidden'} employment-card-mobile`"
            >
              <div class="items-left flex flex-col">
                <div class="card-details">
                  <div class="mb-1 font-medium">
                    {{ getFullName(loanApplicants[member.applicantId]) }}
                    <span v-if="member.roleLabel" class="font-normal text-gray-500">· {{ member.roleLabel }}</span>
                    <span v-if="member.partnerName" class="font-normal text-gray-500"
                      >(Married to {{ member.partnerName }})</span
                    >
                  </div>
                  <div class="items-left flex gap-2">
                    <div class="h-5 w-5">
                      <CheckIcon
                        v-if="financials[member.applicantId]?.income?.employment.length"
                        class="text-green-600"
                      />
                      <XMarkIcon v-else class="text-rose-600" />
                    </div>
                    Employment details
                  </div>
                  <div class="items-left flex gap-2">
                    <div class="h-5 w-5">
                      <CheckIcon
                        v-if="financials[member.applicantId]?.income?.expectIncomeChange !== undefined"
                        class="text-green-600"
                      />
                      <XMarkIcon v-else class="text-rose-600" />
                    </div>
                    Additional income
                  </div>
                </div>
                <div class="employment-descriptions ml-6">
                  <div>
                    <div v-for="description in cardDescription(member.applicantId)" :key="description">
                      <span class="text-gray-400">{{ description }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div v-if="canEditApplicantIncome(member.applicantId)" class="card-button">
                <BxButton
                  :id="member.applicantId + '-income-add-source'"
                  variant="secondary"
                  :disabled="
                    (activeTab && activeTab !== member.applicantId) ||
                    (loanApplicants[member.applicantId].type === 'primary' && isSecondaryDeclaration)
                  "
                  @click="openIncomeForm(member.applicantId)"
                >
                  {{ financials[member.applicantId]?.income?.employment.length ? 'Edit' : 'Add' }} income
                </BxButton>
              </div>
            </BxCard>
          </div>
        </div>
      </div>
      <span v-if="error" class="block text-sm text-rose-500">
        {{ error }}
      </span>
    </div>

    <div class="mt-6">
      <BxButton id="income-continue" variant="primary" @click="saveAndContinue()">Save and continue</BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.employment-descriptions {
  @media (max-width: 767px) {
    margin-top: 20px;
    margin-bottom: 20px;
  }
}
.employment-card {
  @media (max-width: 767px) {
    display: none;
  }
}

.employment-card-mobile {
  @media (min-width: 767px) {
    display: none;
  }
}

.card-button {
  flex-grow: 1;
  @media (min-width: 767px) {
    text-align: right;
  }
  @media (max-width: 767px) {
    text-align: center;
  }
  flex-basis: 200px;
}
</style>
